import { Button, Checkbox, Modal, Popconfirm, notification } from "antd";
import SidebarLayout from "components/Layout/Layout";
import dayjs from "dayjs";
import NewRoleModalContent from "pages/Role/AddNewRoleGroup";
import AddUsersToRoleInterface from "pages/Role/AddUserToRole";
import { useState, useEffect, useMemo } from "react";
import type { NotificationArgsProps } from 'antd';
import React from "react";
import { useTheme } from "components/provider/theme";
import { API_URL } from "env";
import { Permission, RoleItem } from ".";

interface PermissionAtribute {
    id: string;
    displayName: string;
    name: string;
    module: string;
    action: string;
    moduleDisplayName: string;
    actionDisplayName: string;
    roles_display: {
        id: string;
        displayName: string;
    }[];
}


const PermissionEdit = ({ permission, role , onClose}: { permission: Permission[], role: RoleItem , onClose: () => void }) => {
    const [roleName, setRoleName] = useState(role.display_name);
    const [roleDescription, setRoleDescription] = useState(role.description || '');
    const [checked, setChecked] = useState(role.is_active);

    const [selectedPermissions, setSelectedPermissions] = useState<string[]>(
        role.permissions.map(p => String(p.id))
    );
    const [addedPermissions, setAddedPermissions] = useState<string[]>([]);
    const [removedPermissions, setRemovedPermissions] = useState<string[]>([]);

    const [roleData, setRoleData] = useState<any[]>([]);
    const [api, contextHolder] = notification.useNotification();
    async function fetchPermissions() {
        try {
            const response = await fetch(`${API_URL}api/v1/be/permissions`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
            });
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const data = await response.json();
            const permissions: PermissionAtribute[] = data.data.items.map((item: any) => ({
                id: String(item.id),
                displayName: item.display_name,
                name: item.name,
                module: item.module ?? '', // fallback to empty string if null
                action: item.action,
                moduleDisplayName: item.module_display ?? '', // fallback to empty string if null
                actionDisplayName: item.action_display ?? '', // fallback to empty string if null
                roles_display: Array.isArray(item.roles_display)
                    ? item.roles_display.map((role: any) => ({
                        id: String(role.id),
                        displayName: role.display_name
                    }))
                    : []
            }));
            const grouped = permissions.reduce((acc, perm) => {
                if (!acc[perm.module]) {
                    acc[perm.module] = {
                        id: perm.module,
                        name: perm.moduleDisplayName,
                        action: []
                    };
                }
                acc[perm.module].action.push({
                    id: perm.id,
                    name: perm.action as 'read' | 'create' | 'update' | 'delete'
                });
                return acc;
            }, {} as Record<string, { id: string; name: string; action: { id: string; name: 'read' | 'create' | 'update' | 'delete' }[] }>);

            setRoleData(
                Object.values(grouped)
                    .map(module => ({
                        id: module.id,
                        module: [module]
                    }))
            );
        } catch (error) {
            console.error('Error fetching permissions:', error);
        }
    }

    function getPermissionChanges() {
        const added = selectedPermissions.filter(id => !role.permissions.some(p => String(p.id) === id));
        const removed = role.permissions
            .map(p => String(p.id))
            .filter(id => !selectedPermissions.includes(id));
        setAddedPermissions(added);
        setRemovedPermissions(removed);
        return { added, removed };
    }

    async function updateRole() {
        const added = selectedPermissions.filter(id => !role.permissions.some(p => String(p.id) === id));
        const removed = role.permissions
            .map(p => String(p.id))
            .filter(id => !selectedPermissions.includes(id));
        try {
            const response = await fetch(`${API_URL}api/v1/be/role/revoke-permission`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    "role_id": Number(role.id),
                    "permission_id": removed.map(id => Number(id))
                })
            });
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const response2 = await fetch(`${API_URL}api/v1/be/role/assign-permission`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    "role_id": Number(role.id),
                    "permission_id": added.map(id => Number(id))
                })
            });
            if (!response2.ok) {
                throw new Error('Network response was not ok');
            }
        }
        catch (error) {
            console.error('Error updating role:', error);
        }
    }
    useEffect(() => {
        fetchPermissions();
    }, []);
    return (
        <div className="p-4">
            <h2 className="text-xl font-semibold mb-4">Edit Role</h2>
            <form>
                <div className="mb-4">
                    <label className="block text-sm font-medium mb-2">Role ID</label>
                    <input
                        type="text"
                        className="w-full p-2 border rounded bg-gray-100"
                        value={role.id}
                        disabled
                    />
                </div>
                <div className="mb-4">
                    <label className="block text-sm font-medium mb-2">Role Name</label>
                    <input
                        type="text"
                        className="w-full p-2 border rounded"
                        placeholder="Enter role name"
                        value={roleName}
                        onChange={(e) => setRoleName(e.target.value)}
                    />
                </div>
                <div className="mb-4">
                    <label className="block text-sm font-medium mb-2">Role Description</label>
                    <textarea
                        className="w-full p-2 border rounded"
                        placeholder="Enter role description"
                        value={roleDescription}
                        onChange={(e) => setRoleDescription(e.target.value)}
                        rows={4}
                    />
                </div>
                <div className="mb-4 inline-flex items-center align-middle gap-2">
                    <label className="block text-sm font-medium">Active</label>
                    <Checkbox
                        checked={
                            checked
                        }
                        onChange={(e) => setChecked(e.target.checked)}
                        className="cursor-pointer"
                    />
                </div>
                <div className="mb-4">
                    <div className="flex flex-col gap-2 max-h-72 overflow-y-auto">
                        <label className="block text-sm font-medium mb-2">Assign Permissions</label>
                        {roleData.map((module) => (
                            <div key={module.id} className="border p-4 rounded">
                                <h3 className="font-semibold mb-2">{module.module[0].name}</h3>
                                <div className="flex flex-wrap gap-4">
                                    {module.module[0].action.map((action: { id: string; name: 'read' | 'create' | 'update' | 'delete' }) => (
                                        <div key={action.id} id={action.id} className="flex items-center gap-2">
                                            <Checkbox
                                                checked={selectedPermissions.includes(action.id)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedPermissions([...selectedPermissions, action.id]);
                                                    } else {
                                                        setSelectedPermissions(selectedPermissions.filter(id => id !== action.id));
                                                    }
                                                }}
                                            />
                                            <span className="capitalize">{action.name}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <Button type="primary" onClick={async () => {
                    await updateRole();
                    onClose();
                }}>
                    Save Role
                </Button>
            </form>
        </div>
    )
}

export default PermissionEdit;